import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import { Row, Col, message } from '@lockerpm/design';

import {
  DeleteOutlined,
  CloseOutlined
} from "@ant-design/icons";

import commonComponents from "../../../components/common";

import CardItem from "./components/CardItem";
import FormData from "./components/FormData";

import global from "../../../config/global";
import storeActions from  '../../../store/actions';
import projectServices from "../../../services/project";
import {
  paginationAndSortData,
  scrollEnd,
} from '../../../utils/common';

const Projects = () => {
  const { PageHeader } = commonComponents;
  const { t } = useTranslation();

  const projects = useSelector((state) => state.project.projects)
  const currentPage = useSelector((state) => state.system.currentPage)
  const wsRole = useSelector((state) => state.workspace.wsRole)
  const createProject = useSelector((state) => state.onboarding.createProject)
  const socketLastMessage = useSelector((state) => state.system.socketLastMessage);

  const pageSize = 15;

  const [formVisible, setFormVisible] = useState(false);
  const [isSelect, setIsSelect] = useState(false);
  const [callingAPI, setCallingAPI] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    size: pageSize,
    name: '',
  });

  useEffect(() => {
    if (createProject) {
      handleOpenForm();
    }
  }, [createProject])

  useEffect(() => {
    if (socketLastMessage) {
      const strData = socketLastMessage?.data.split("'").join('"')
      const messageData = JSON.parse(strData)
      if (messageData.event === 'sync') {
        syncData();
      }
    }
  }, [socketLastMessage]);

  const wsProjects = useMemo(() => {
    return projects.filter((p) => p.organization.id == currentPage.params?.workspace_id)
  }, [projects, currentPage])

  const filteredData = useMemo(() => {
    return paginationAndSortData(
      wsProjects,
      params,
      params.sortBy,
      params.sortType,
      [
        (d) => !params.name || d.name?.toLowerCase()?.includes(params.name?.toLowerCase()),
      ]
    )
  }, [wsProjects, params])

  const handleOpenForm = (item = null) => {
    setSelectedItem(item);
    setFormVisible(true)
  }

  const handleSelect = async (project) => {
    if (!isSelect) {
      global.navigate('PROJECT_SECRETS', { workspace_id: currentPage.params?.workspace_id, project_id: project.id })
    } else if (global.constants.PROJECT_MEMBER_ROLE.ADMIN !== project.role) {
      message.warning(t('notification.warning.project.select'))
    } else {
      if (selectedProjects.find((p) => p.id === project.id)) {
        setSelectedProjects(selectedProjects.filter((p) => p.id !== project.id))
      } else {
        setSelectedProjects([...selectedProjects, project]);
      }
    }
  }

  const syncData = async () => {
    global.store.dispatch(storeActions.updateIsFetchProjects(true));
  }

  const handleDeleteProjects = () => {
    global.confirm(() => {
      setCallingAPI(true)
      const projectIds = selectedProjects.map((p) => p.id)
      projectServices.remove_projects(projectIds).then(async () => {
        global.notification('success', t('notification.success.title'), t('notification.success.project.deleted'));
        global.store.dispatch(storeActions.updateProjects(projects.filter((p) => !projectIds.includes(p.id))))
        setSelectedProjects([])
        setCallingAPI(false)
        setIsSelect(false)
      }).catch((error) => {
        setCallingAPI(false)
        global.pushError(error)
      });
    });
  };
  
  return (
    <div
      className="projects layout-content"
      onScroll={(e) => scrollEnd(e, params, filteredData.total, setParams, true, pageSize)}
    >
      <PageHeader
        title={`${t('projects.title')} (${wsProjects.length})`}
        subtitle={t('projects.description')}
        actions={[
          {
            key: 'select',
            label: `${t('button.select')} ${t('projects.title')}`,
            type: 'primary',
            hide: isSelect || global.constants.WS_MEMBER_ROLE.ADMIN !== wsRole || !wsProjects.length,
            click: () => setIsSelect(true)
          },
          {
            key: 'cancel',
            label: t('button.cancel'),
            hide: !isSelect || global.constants.WS_MEMBER_ROLE.ADMIN !== wsRole,
            icon: <CloseOutlined />,
            disabled: callingAPI,
            click: () => {
              setSelectedProjects([]);
              setIsSelect(false)
            }
          },
          {
            key: 'delete',
            label: `${t('button.delete')} ${selectedProjects.length} ${t('projects.title')}`,
            type: 'primary',
            danger: true,
            hide: selectedProjects.length === 0 || global.constants.WS_MEMBER_ROLE.ADMIN !== wsRole,
            icon: <DeleteOutlined />,
            loading: callingAPI,
            click: () => handleDeleteProjects()
          }
        ]}
      />
      <Row gutter={[24, 24]} className="mt-4">
        <Col
          xl={6}
          lg={8}
          md={8}
          sm={12}
          xs={24}
        >
          <CardItem
            isCreate={true}
            onCreate={() => handleOpenForm()}
          />
        </Col>
        {
          filteredData.result.map((project) => <Col
            key={project.id}
            xl={6}
            lg={8}
            md={8}
            sm={12}
            xs={24}
          >
            <CardItem
              item={project}
              isSelect={isSelect}
              isSelected={selectedProjects.find((p) => p.id === project.id)}
              onSelect={handleSelect}
            />
          </Col>)
        }
      </Row>
      <FormData
        visible={formVisible}
        item={selectedItem}
        onClose={() => setFormVisible(false)}
        onReload={() => syncData()}
      />
    </div>
  );
}

export default Projects;